import {
  FARADAY_CONSTANT,
  SECONDS_PER_MINUTE,
  buildGalvanicCell,
  formatSigned,
  fullEquationText,
  type ElectrolysisCase,
  type GalvanicCell,
} from './electrochemicalCells';
import {
  type ExploreChallenge,
  type ModelField,
  type WorkedStep,
  firstModelError,
  formatModelNumber,
} from './modelInputContract';

/**
 * Explore calculations for the Electrochemical Cells Lab (chemistry/CLAUDE.md "Interactive
 * calculation standard"). The learner types the current and time for electrolysis, or picks two
 * half-cells for a galvanic cell; every view reads the worked steps and scene state from here.
 */

export interface ElectrolysisSetup {
  currentA: number;
  timeMin: number;
}

export type ElectrolysisInput = keyof ElectrolysisSetup;

/** Teaching range for a school electrolysis cell with a bench DC power pack. */
export const ELECTROLYSIS_FIELDS: ModelField<ElectrolysisInput>[] = [
  { key: 'currentA', label: 'Current', unit: 'A', min: 0.1, max: 10, step: 0.1 },
  { key: 'timeMin', label: 'Time', unit: 'min', min: 1, max: 120, step: 1 },
];

export const DEFAULT_ELECTROLYSIS_SETUP: ElectrolysisSetup = { currentA: 2, timeMin: 30 };

/**
 * Molar volume of an ideal gas at 25 °C (298.15 K) and 100 kPa: V = RT ÷ p
 * = 8.314 J/(mol·K) × 298.15 K ÷ 100 kPa = 24.79 L/mol (IUPAC standard ambient conditions;
 * matches STANDARD_CONDITIONS in electrochemicalCellsData.ts). Review status: pending-teaching-review.
 */
export const MOLAR_GAS_VOLUME_L_PER_MOL = 24.79;
export const EXPLORE_REVIEW_STATUS = 'pending-teaching-review' as const;

const MAX_FIELD = Object.fromEntries(ELECTROLYSIS_FIELDS.map((field) => [field.key, field.max])) as Record<ElectrolysisInput, number>;
/** Largest charge the Explore range allows, used to scale the deposit and gas in the 3D view. */
const MAX_ELECTRON_MOL = (MAX_FIELD.currentA * MAX_FIELD.timeMin * SECONDS_PER_MINUTE) / FARADAY_CONSTANT;

/** Three significant figures in Australian English, for example 0.0373 or 3,600. */
export function sig3(value: number): string {
  return value.toLocaleString('en-AU', { maximumSignificantDigits: 3 });
}

export type ElectrolyteLook = 'blue' | 'colourless' | 'molten';
export type CathodeLook = 'copper' | 'silver' | 'metal-pool-bottom' | 'metal-pool-top' | 'gas';
export type AnodeLook = 'gas' | 'dissolves' | 'none';

/** Everything the 3D electrolysis cell shows; built only from the Explore result. */
export interface ElectrolysisSceneState {
  electrolyte: ElectrolyteLook;
  electrodes: 'inert' | 'copper';
  cathode: CathodeLook;
  anode: AnodeLook;
  /** Share (0–1) of the largest deposit or gas amount the Explore range can make. */
  amount: number;
  /** Share (0–1) of the largest current; sets the bubble rate. */
  currentShare: number;
}

export interface ElectrolysisExploreResult {
  chargeC: number;
  electronMol: number;
  productMol: number;
  massG: number;
  /** Gas volume at 25 °C and 100 kPa when the counted product is a gas; otherwise null. */
  gasVolumeL: number | null;
  steps: WorkedStep[];
  result: string;
  limitNote: string | null;
  scene: ElectrolysisSceneState;
}

/** Molten metals collect at the bottom when denser than the melt (lead, aluminium) and float when less dense (sodium, magnesium). */
const MOLTEN_METAL_SINKS: Record<string, boolean> = { sodium: false, lead: true, aluminium: true, magnesium: false };

function sceneFor(item: ElectrolysisCase, electronMol: number, currentA: number): ElectrolysisSceneState {
  const cathodeId = item.cathode.productId;
  let cathode: CathodeLook;
  if (cathodeId === 'hydrogen') cathode = 'gas';
  else if (item.kind === 'molten') cathode = MOLTEN_METAL_SINKS[cathodeId] ? 'metal-pool-bottom' : 'metal-pool-top';
  else cathode = cathodeId === 'silver' ? 'silver' : 'copper';
  const anodeId = item.anode.productId;
  const anode: AnodeLook = anodeId === 'copper-dissolves' ? 'dissolves' : ['oxygen', 'chlorine', 'bromine'].includes(anodeId) ? 'gas' : 'none';
  return {
    electrolyte: item.kind === 'molten' ? 'molten' : item.id.startsWith('aqueous-cuso4') ? 'blue' : 'colourless',
    electrodes: item.id === 'aqueous-cuso4-copper' ? 'copper' : 'inert',
    cathode,
    anode,
    amount: electronMol / MAX_ELECTRON_MOL,
    currentShare: currentA / MAX_FIELD.currentA,
  };
}

/** Faraday's law with the learner's numbers. Throws the first input error (fail closed). */
export function calculateElectrolysisExplore(item: ElectrolysisCase, setup: ElectrolysisSetup): ElectrolysisExploreResult {
  const error = firstModelError(ELECTROLYSIS_FIELDS, setup);
  if (error) throw new Error(error);
  const { currentA, timeMin } = setup;
  const { productLabel, molarMassGmol, electronsPerFormula } = item.faraday;
  const timeS = timeMin * SECONDS_PER_MINUTE;
  const chargeC = currentA * timeS;
  const electronMol = chargeC / FARADAY_CONSTANT;
  const productMol = electronMol / electronsPerFormula;
  const massG = productMol * molarMassGmol;
  const isGas = item.cathode.productId === 'hydrogen';
  const gasVolumeL = isGas ? productMol * MOLAR_GAS_VOLUME_L_PER_MOL : null;
  const F = formatModelNumber(FARADAY_CONSTANT, 0);

  const steps: WorkedStep[] = [
    {
      id: 'charge',
      title: 'Charge that flows',
      formula: 'Q = I × t',
      working: `Q = ${formatModelNumber(currentA, 2)} A × (${formatModelNumber(timeMin, 0)} min × 60 s/min) = ${formatModelNumber(currentA, 2)} A × ${formatModelNumber(timeS, 0)} s = ${formatModelNumber(chargeC, 0)} C`,
      status: 'done',
    },
    {
      id: 'electrons',
      title: 'Moles of electrons',
      formula: 'n(e⁻) = Q ÷ F',
      working: `n(e⁻) = ${formatModelNumber(chargeC, 0)} C ÷ ${F} C/mol = ${sig3(electronMol)} mol`,
      status: 'done',
    },
    {
      id: 'product',
      title: `Moles of ${productLabel}`,
      formula: 'n(product) = n(e⁻) ÷ z',
      working: `n(${productLabel}) = ${sig3(electronMol)} mol ÷ ${electronsPerFormula} = ${sig3(productMol)} mol`,
      status: 'done',
      note: `z = ${electronsPerFormula} e⁻ per formula unit`,
    },
    {
      id: 'mass',
      title: `Mass of ${productLabel}`,
      formula: 'm = n × M',
      working: `m = ${sig3(productMol)} mol × ${molarMassGmol} g/mol = ${sig3(massG)} g`,
      status: 'done',
    },
  ];
  if (gasVolumeL !== null) {
    steps.push({
      id: 'volume',
      title: `Volume of ${productLabel}`,
      formula: 'V = n × Vm (25 °C, 100 kPa)',
      working: `V = ${sig3(productMol)} mol × ${MOLAR_GAS_VOLUME_L_PER_MOL} L/mol = ${sig3(gasVolumeL)} L`,
      status: 'done',
    });
  }

  const atLimit = currentA >= MAX_FIELD.currentA || timeMin >= MAX_FIELD.timeMin;
  return {
    chargeC,
    electronMol,
    productMol,
    massG,
    gasVolumeL,
    steps,
    result: `Result: ${sig3(massG)} g of ${productLabel}${gasVolumeL !== null ? ` (${sig3(gasVolumeL)} L at 25 °C, 100 kPa)` : ''} at the cathode`,
    limitNote: atLimit
      ? `This is the top of the model range (${MAX_FIELD.currentA} A, ${MAX_FIELD.timeMin} min). Larger currents heat the cell and waste charge on side reactions, so the model stops here instead of extrapolating.`
      : null,
    scene: sceneFor(item, electronMol, currentA),
  };
}

/** What the Faraday tasks read: the counted product, the current and the Explore result. */
export interface FaradayChallengeInput {
  productId: string;
  currentA: number;
  result: ElectrolysisExploreResult;
}

export const FARADAY_QUESTION = 'What decides how much metal builds up on the cathode?';

const inRange = (value: number, low: number, high: number) => value >= low && value <= high;
/** ±1 % of the doubled default charge (2 × 3,600 C). */
const DOUBLE_CHARGE_C = 2 * DEFAULT_ELECTROLYSIS_SETUP.currentA * DEFAULT_ELECTROLYSIS_SETUP.timeMin * SECONDS_PER_MINUTE;

/** Teaching tasks (CT-INTERACT C7). Each check reads only the Explore result, never the UI. */
export const FARADAY_CHALLENGES: ExploreChallenge<FaradayChallengeInput>[] = [
  {
    id: 'double-time',
    prompt: 'Double the mass at the cathode by changing only the time.',
    hint: `Keep ${DEFAULT_ELECTROLYSIS_SETUP.currentA} A. The mass follows the charge, Q = I × t.`,
    isMet: ({ currentA, result }) =>
      Math.abs(currentA - DEFAULT_ELECTROLYSIS_SETUP.currentA) < 1e-9 &&
      inRange(result.chargeC, DOUBLE_CHARGE_C * 0.99, DOUBLE_CHARGE_C * 1.01),
    why: '2 A for 60 min passes 7,200 C, twice the 3,600 C of 30 min, so twice the moles of electrons reach the cathode and twice the mass forms: mass ∝ I × t.',
  },
  {
    id: 'copper-gram',
    prompt: 'Choose a copper(II) sulfate cell and plate 1.00 g of copper (0.95–1.05 g).',
    hint: 'Work backwards: n(Cu) = 1.00 ÷ 63.55, n(e⁻) = 2 × n(Cu), then Q = n(e⁻) × F.',
    isMet: ({ productId, result }) => productId === 'copper' && inRange(result.massG, 0.95, 1.05),
    why: '1.00 g of copper is 0.0157 mol. Each Cu²⁺ takes 2 electrons, so you need 0.0315 mol e⁻ = about 3,040 C, for example 2 A for 25 min.',
  },
  {
    id: 'silver-gram',
    prompt: 'Now switch to silver nitrate and plate 1.00 g of silver (0.95–1.05 g). Does it need more or less charge than copper?',
    hint: 'Ag⁺ needs only 1 electron for each silver atom.',
    isMet: ({ productId, result }) => productId === 'silver' && inRange(result.massG, 0.95, 1.05),
    why: '1.00 g of silver is 0.00927 mol and each Ag⁺ takes only 1 electron, so about 894 C is enough (for example 1 A for 15 min), about 3.4 times less than for 1.00 g of copper. The charge on the ion (z) and the molar mass both matter.',
  },
];

export interface GalvanicExploreResult {
  cell: GalvanicCell;
  steps: WorkedStep[];
  result: string;
}

/** E°cell for two reviewed half-cells with the numbers substituted. Throws the model's reason when the pair is not supported. */
export function calculateGalvanicExplore(firstId: string, secondId: string): GalvanicExploreResult {
  const outcome = buildGalvanicCell(firstId, secondId);
  if (outcome.status !== 'ok') throw new Error(outcome.reason);
  const { cathode, anode, eCellV, electronsTransferred, overall, spontaneous } = outcome;
  const steps: WorkedStep[] = [
    {
      id: 'cathode',
      title: 'Find the cathode',
      formula: 'the higher E° is reduced',
      working: `${cathode.label}: ${formatSigned(cathode.e0V)} V > ${anode.label}: ${formatSigned(anode.e0V)} V, so ${cathode.label} is the cathode (+) and ${anode.label} is the anode (−)`,
      status: 'done',
    },
    {
      id: 'ecell',
      title: 'Cell potential',
      formula: 'E°cell = E°(cathode) − E°(anode)',
      working: `E°cell = ${formatSigned(cathode.e0V)} V − (${formatSigned(anode.e0V)} V) = ${formatSigned(eCellV)} V`,
      status: 'done',
    },
    {
      id: 'overall',
      title: 'Overall reaction',
      formula: 'electrons lost = electrons gained',
      working: `${electronsTransferred} e⁻ cancel: ${fullEquationText(overall)}`,
      status: 'done',
    },
  ];
  return {
    cell: outcome,
    steps,
    result: `Result: E°cell = ${formatSigned(eCellV)} V · ${spontaneous ? 'positive, so spontaneous as written' : 'not positive, so no spontaneous reaction'}`,
  };
}

export const GALVANIC_QUESTION = 'What sets the voltage of a galvanic cell?';

/** Teaching tasks (CT-INTERACT C7). Each check reads only the Explore result, never the UI. */
export const GALVANIC_CHALLENGES: ExploreChallenge<GalvanicExploreResult>[] = [
  {
    id: 'copper-anode',
    prompt: 'Make copper the anode: the half-cell that is oxidised and loses electrons.',
    hint: 'Pair Cu²⁺/Cu with a half-cell that is higher in the table.',
    isMet: ({ cell }) => cell.anode.id === 'copper',
    why: 'No metal is always the cathode. Against silver, Cu²⁺/Cu (+0.34 V) is the lower E°, so copper is oxidised and E°cell = 0.80 − 0.34 = 0.46 V.',
  },
  {
    id: 'over-three-volts',
    prompt: 'Build a cell with E°cell above 3.00 V.',
    hint: 'Look for the biggest gap in the table: a very low E° against a very high one.',
    isMet: ({ cell }) => cell.eCellV > 3,
    why: 'E°cell is the gap between the two E° values. Magnesium (−2.37 V) with chlorine (+1.36 V) gives 1.36 − (−2.37) = 3.73 V, more than three times a Zn–Cu cell (1.10 V).',
  },
  {
    id: 'unequal-electrons',
    prompt: 'Pick two half-cells that take different numbers of electrons. Does E° double when a half-equation is doubled?',
    hint: 'Ag⁺/Ag takes 1 e⁻; Cu²⁺/Cu and Zn²⁺/Zn take 2.',
    isMet: ({ cell }) => cell.cathode.reduction.electrons !== cell.anode.reduction.electrons,
    why: 'You multiply a half-equation to cancel the electrons (2Ag⁺ + 2e⁻ → 2Ag), but E° is not multiplied: Zn–Ag gives 0.80 − (−0.76) = 1.56 V. E° is a property of each electron transfer, not of the amount.',
  },
];
